import {
  Coords,
  DragConstraintInstance,
  HandShapeConfig,
  HandShapeNewDescriptor,
  ShapeControlPointPosition,
} from '../../../../../elements/resource/types/shape.type';
import { PathShape, PathShapeConfig } from '../path-shape';
import { PathItem } from '../path-shape.types';
import { selectHandShapeConfig } from '../../../../store/selector/editor.selector';
import { cloneDeep, isEqual, omit } from 'lodash';
import { GeneralShape } from '../../general/general-shape';
import { ShapeService } from '../../../shape.service';
import { ResourceData } from '../../../../../elements/resource/resource.types';
import { HandSectionNext } from './hand-section-next';
import { PathElement } from '../../primitive/path-element';
import { selectCurrentAnimationId } from '../../../../animation/store/animation.selector';
import { setDescriptorValue } from '../../../../store/editor.crud.actions';

export interface HandShapeSectionDescriptorNew {
  x: number;
  y: number;
  r: number;
  flat?: boolean;
  corner?: boolean;
  d1?: number;
  d2?: number;
  cornerRadius?: number;
  end?: 'flat' | 'arc' | 'half-arc' | 'half-arc-inverse';
  joint?: 'symm' | 'assym' | 'assym-inverse';
  stroke?: {
    color: string;
    width: number;
    top?: { start: number; end: number };
    side?: { start: number; end: number };
    bottom?: { start: number; end: number };
  };
  curve?: number;
}

const { PI, sin, cos, pow, sqrt } = Math;

export class HandShapeNext extends PathShape<HandShapeNewDescriptor> {
  get isClosed() {
    return this.handIsClosed;
  }
  handConfig: HandShapeConfig;
  originalConfig: HandShapeConfig;
  handIsClosed: boolean;
  handSections: HandSectionNext[];
  getType(): string {
    return 'hand-shape-next';
  }
  get lastSectionIndex() {
    return this.handSections.length - 1;
  }
  get lastHandSection() {
    return this.handSections[this.lastSectionIndex];
  }

  get firstHandSection() {
    return this.handSections[0];
  }
  get lastButOneHandSection() {
    return this.handSections[this.lastSectionIndex - 1];
  }

  constrainedShape: GeneralShape;
  constraintPoint: ShapeControlPointPosition;
  handSectionDescriptors: HandShapeSectionDescriptorNew[];
  curve: number;

  _config: HandShapeConfig;

  constructor(
    service: ShapeService,
    data: ResourceData<HandShapeNewDescriptor>,
    config: PathShapeConfig,
  ) {
    super(service, data, config);
    this.strokeElements = [];
    // console.log('hand-shape-next', this.descriptor.config);
    if (!this.descriptor.config) {
      return;
    }

    this.handIsClosed = !!this.descriptor.config.closed;
    this.curve = this.descriptor.config.curve;
    this.originalConfig = this.descriptor.config;
    this.handSectionDescriptors = this.descriptor.config.handSections;

    this.store
      .select(selectCurrentAnimationId)
      .subscribe(id => (this.currentAnimationId = id));
    this.store.select(selectHandShapeConfig(this.IRI)).subscribe(config => {
      if (!isEqual(this.handConfig, config)) {
        this.handConfig = config;

        const { handSections, closed, curve } = config || {};
        if (!handSections?.length) {
          return;
        }

        if (
          !isEqual(this.handSectionDescriptors, handSections) ||
          this.curve !== curve ||
          this.handIsClosed !== closed
        ) {
          if (!this.currentAnimationId) {
            this.handIsClosed = closed;
            this.curve = curve;
          }
          // console.log('reinit-handsections', handSections);
          this.handSectionDescriptors = cloneDeep(handSections);
          this.initHandSections();
          this.showDragControllers();
          this.refreshElement();
        }

        if (!this.currentAnimationId) {
          this.handIsClosed = closed;
          this.curve = curve;
        }
      }
    });

    if (this.editable) {
      this.cs.keyDownEventSubscribe('c', () => {
        if (this.selected) {
          return;
        }
        this.handSections?.map(hs => hs.pc.show());
      });

      this.cs.keyEventSubscribe('c', () => {
        if (this.selected) {
          return;
        }
        this.handSections?.map(hs => hs.pc.hide());
      });
    }

    this.initHandSections();
  }

  hoverSelect() {
    this.baseLine.show();
    this.handSections.map(hs => hs.pc.show());
  }

  hoverDeselect(): void {
    if (this.selected) {
      return;
    }
    this.baseLine.hide();
    this.handSections.map(hs => hs.pc.hide());
  }

  // applyAnimationByTime(time: number, directApply = false) {
  //   if (time == undefined) {
  //     return;
  //   }

  //   let changed = false;
  //   const updates = super.applyAnimationByTime(time, directApply);
  //   Object.entries(this.consequtiveAnimationsByKey || {}).map(
  //     ([animationKey]) => {
  //       switch (animationKey) {
  //         case 'config':
  //           const config =
  //             this.animationService.getShapeAttributeTillTime<HandShapeConfig>(
  //               time,
  //               this.IRI,
  //               this.originalConfig,
  //               'config',
  //             );
  //           if (directApply) {
  //             changed = true;
  //             this.handConfig = config;
  //           }
  //           updates.config = config;
  //           break;
  //       }
  //     },
  //   );

  //   if (changed) {
  //     this.refreshElement();
  //   }

  //   return updates;
  // }

  startAnimation(
    id: string,
    division: number,
    inverse?: boolean,
    duration?: number,
  ): void {
    super.startAnimation(id, division, inverse, duration);
    this.getAnimationsById(id).map(({ key, value }) => {
      switch (key) {
        case 'config':
          const { handSections } = value as HandShapeConfig;
          this.handSections.map((hs, i) =>
            hs.startAnimation(handSections[i], division),
          );
          break;
      }
    });
  }

  rotateDrag = false;

  _startDragBy(
    point: string,
    shapeStore: Record<string, boolean>,
    { rotate, direct }: { rotate?: boolean; direct?: boolean } = {},
  ): DragConstraintInstance {
    // -- // -- //

    shapeStore[this.IRI] = true;

    this.rotateDrag = rotate;

    const [, _index, subIndex] = point.split('.');

    const index = +[_index, subIndex].filter(v => !!v).join('.');

    this.dragBase = [this.x, this.y];

    const section = this.handSections[index];

    let constraints = this.getConstraintsByPoint(point, shapeStore, { direct });

    const limit = constraints.find(({ limit }) => !!limit)?.limit;
    constraints = constraints.map(c => omit(c, 'limit'));

    const [section0, section1, section2, section3] = this.handSections;

    this.handSections.map(hs => hs.startDrag(shapeStore));
    // -- //
    if (this.isClosed) {
      switch (index) {
        case 0:
          if (this.cs.isShiftPressed) {
            // -- // -- //
            return {
              shapeIRI: this.IRI,
              point,
              constraints: [
                {
                  shapeIRI: this.IRI,
                  point: 'section.1',
                  inverse: true,
                },
                ...constraints,
              ],
            };
          } else {
            return {
              shapeIRI: this.IRI,
              point,
              // limit: {
              //   x: -section1.x,
              //   y: -section1.y,
              //   l: section1.l,
              // },
              constraints: [
                {
                  shapeIRI: this.IRI,
                  point: 'section.1',
                  inverse: true,
                },
                ...constraints,
                {
                  point: 'section.3',
                  shapeIRI: this.IRI,
                  noAngle: true,
                  constraints: this.getConstraintsByPoint(
                    'section.3',
                    shapeStore,
                  ),
                },
              ],
            };
          }

        case 1:
          if (this.cs.isShiftPressed) {
            return {
              shapeIRI: this.IRI,
              point,
              constraints: [
                ...constraints,
                {
                  shapeIRI: this.IRI,
                  point: 'section.2',
                  inverse: true,
                },
              ],
            };
          } else {
            return {
              shapeIRI: this.IRI,
              point,
              // limit: this.cs.isShiftPressed
              //   ? undefined
              //   : {
              //       x: section1.x,
              //       y: section1.y,
              //       l: sqrt(pow(section1.x, 2) + pow(section1.y, 2)),
              //     },
              constraints: [
                ...constraints,
                ...this.getConstraintsByPoint('section.2', shapeStore),
                {
                  shapeIRI: this.IRI,
                  point: 'section.2.50',
                  inverse: true,
                  constraints: this.getConstraintsByPoint(
                    'section.2.50',
                    shapeStore,
                  ),
                },
                {
                  shapeIRI: this.IRI,
                  point: 'section.3',
                  inverse: true,
                },
              ],
            };
          }

        case 2:
          if (this.cs.isShiftPressed) {
            return {
              shapeIRI: this.IRI,
              point,
              constraints: [
                ...constraints,
                {
                  shapeIRI: this.IRI,
                  point: 'section.3',
                  inverse: true,
                },
              ],
            };
          } else {
            return {
              shapeIRI: this.IRI,
              point,
              noTranslate: true,
              // limit: this.cs.isShiftPressed
              //   ? undefined
              //   : {
              //       x: -section3.x,
              //       y: -section3.y,
              //       l: section3.l,
              //     },
              constraints: [
                ...constraints,
                {
                  shapeIRI: this.IRI,
                  point: 'section.1',
                  noAngle: true,
                  constraints: this.getConstraintsByPoint(
                    'section.1',
                    shapeStore,
                  ),
                },
                {
                  shapeIRI: this.IRI,
                  point: 'section.2.50',
                  noAngle: true,
                  constraints: this.getConstraintsByPoint(
                    'section.2.50',
                    shapeStore,
                  ),
                },
                {
                  shapeIRI: this.IRI,
                  point: 'section.3',
                  inverse: true,
                },
              ].filter(v => !!v),
            };
          }
        case 2.5:
          if (this.cs.isShiftPressed) {
            return {
              shapeIRI: this.IRI,
              point,
              constraints: [
                ...constraints,
                {
                  shapeIRI: this.IRI,
                  point: 'section.3',
                  inverse: true,
                },
              ],
            };
          } else {
            return {
              shapeIRI: this.IRI,
              point,
              noTranslate: true,
              constraints: [
                ...constraints,
                {
                  shapeIRI: this.IRI,
                  point: 'section.1',
                  noAngle: true,
                  constraints: this.getConstraintsByPoint(
                    'section.1',
                    shapeStore,
                  ),
                },
                ...this.getConstraintsByPoint('section.2', shapeStore),
                {
                  shapeIRI: this.IRI,
                  point: 'section.3',
                  inverse: true,
                },
              ].filter(v => !!v),
            };
          }

        case 3:
          if (this.cs.isShiftPressed) {
            return {
              shapeIRI: this.IRI,
              point,
            };
          } else {
            const section1 = this.handSections[1];
            section1.startDrag();
            const section3 = this.handSections[3];
            section3.startDrag();
            return {
              shapeIRI: this.IRI,
              point,
              limit: {
                x: -section1.x,
                y: -section1.y,
                l: section1.l,
              },
              constraints: [
                ...constraints,
                {
                  point: 'section.0',
                  shapeIRI: this.IRI,
                  noAngle: true,
                  constraints: this.getConstraintsByPoint(
                    'section.0',
                    shapeStore,
                  ),
                },
                {
                  shapeIRI: this.IRI,
                  point: 'section.1',
                  inverse: true,
                },
              ],
            };
          }
      }
    } else {
      switch (index) {
        case 0:
          if (this.cs.isShiftPressed) {
            return {
              shapeIRI: this.IRI,
              point: 'section.0',
              constraints: [
                ...constraints,
                {
                  shapeIRI: this.IRI,
                  point: 'section.1',
                  inverse: true,
                },
              ],
            };
          } else {
            return {
              shapeIRI: this.IRI,
              point: 'section.0',
              // limit: this.cs.isShiftPressed ? undefined : limit,
              constraints: [
                ...constraints,
                ...this.handSections.slice(1).reduce((array, section) => {
                  array.push(
                    ...this.getConstraintsByPoint(
                      section.id,
                      {},
                      {
                        noAngle: true,
                      },
                    ),
                  );
                  return array;
                }, []),
              ],
            };
          }

        case 1:
          const section1 = this.handSections[index];
          section1.startDrag(shapeStore, true);

          return {
            shapeIRI: this.IRI,
            point,
            limit: this.cs.isShiftPressed
              ? undefined
              : {
                  x: section1.x,
                  y: section1.y,
                  l: sqrt(pow(section1.x, 2) + pow(section1.y, 2)),
                },
            constraints: [
              {
                shapeIRI: this.IRI,
                point: 'section.2',
                angle: true,
                noTranslate: true,
                constraints: this.getConstraintsByPoint(
                  'section.2',
                  shapeStore,
                ),
              },
              ...constraints,
            ],
          };

        case 2:
          section.startDrag(shapeStore, true);

          return {
            shapeIRI: this.IRI,
            point,
            limit: this.cs.isShiftPressed
              ? undefined
              : {
                  x: section.x,
                  y: section.y,
                  l: sqrt(pow(section.x, 2) + pow(section.y, 2)),
                },
            constraints,
          };
      }
    }
  }

  dragNew([dx, dy]: Coords, dAngle?: number) {
    // console.log('drag-new', dx, dy);
    const dragsByObject = this.currentDragConstraints.reduce(
      (object, item) => {
        object[item.shapeIRI] ||= [];
        object[item.shapeIRI].push(item);
        return object;
      },
      {} as Record<string, DragConstraintInstance[]>,
    );

    Object.entries(dragsByObject).map(([shapeIRI, array]) => {
      const shape =
        shapeIRI == this.IRI ? this : this.service.getShapeByIRI(shapeIRI);
      shape.applyDrag(array, [dx, dy], dAngle);
    });
  }

  applyDrag(
    instances: DragConstraintInstance[],
    [dx, dy]: Coords,
    dAngle?: number,
  ) {
    // -- // -- // -- //
    let refreshElement = false;
    for (const { point, inverse } of instances) {
      // console.log('applyDrag', { point, inverse }, [dx, dy]);
      const index = +point.split('.')?.[1];
      switch (index) {
        // -- //
        case 0:
          const [x, y] = this.dragBase;
          this.applyTranslate({
            x: x + dx,
            y: y + dy,
          });
          break;
        default:
          refreshElement = true;
          if (inverse) {
            this.handSections[index].baseDrag(-dx, -dy);
          } else {
            this.handSections[index].baseDrag(dx, dy);
          }
      }
    }

    if (refreshElement) {
      this.refreshElement();
    }
  }

  dragBy(point: string, [dx, dy]: Coords, dAngle = 0): void {
    this.currentDragConstraints.map(({ shapeIRI, inverse, point }) => {
      const shape = this.service.getShapeByIRI(shapeIRI);

      if (inverse) {
        shape.dragBy(point, [-dx, -dy]);
      } else {
        shape.dragBy(point, [dx, dy]);
      }
    });
  }

  endDragBy(point: string, [dx, dy] = this._diffs) {
    const index = +point.split('.')[1];
    this.rotateDrag = false;

    if (this.isClosed && !this.cs.isShiftPressed && this.index == 3) {
      this.handSections[0].endDrag();
    }
    this.handSections[index].endDrag();
    this.saveSections();
  }

  incrementAnimation(increment: number, id?: string, percent?: number): void {
    super.incrementAnimation(increment, id, percent);
    this.getAnimationsById(id).map(({ key }) => {
      switch (key) {
        case 'config':
          this.handSections.map(hs => hs.incrementAnimation(increment));
          this.refreshElement();
          break;
      }
    });
  }

  initHandSections() {
    // console.log('init-hand-sections', this.handSectionDescriptors); //
    this.handSections?.map(hs => hs.remove());
    this.handSections = this.handSectionDescriptors?.map(
      (config, i) => new HandSectionNext(this, config, i),
    );

    // TODO - this is probably not that optimal
    this.refreshElement();
    this.handSections.map(hs => hs.afterInit());
  }

  getAbsCoordsOfControlPoint(pointId: string): number[] {
    if (pointId.startsWith('section')) {
      console.log('pointId', pointId);
      const [, index, subIndex] = pointId.split('.');
      const section = this.handSections[index];

      let [ox, oy] = [0, 0];
      if (subIndex) {
        switch (subIndex) {
          case '50':
            ox = -this.handSections[index].x / 2;
            oy = -this.handSections[index].y / 2;
            break;
        }
      }

      return [this.x + section.absX + ox, this.y + section.absY + oy];
    }
    return super.getAbsCoordsOfControlPoint(pointId);
  }
  updateSection(index: number, section: HandShapeSectionDescriptorNew) {
    this.handSectionDescriptors[index] = section;
    this.saveSections();
  }
  baseConfigs: Coords[];

  firstSectionDrag = false;
  dragSectionStartIndex: number;
  dragSectionEndIndex: number;

  _startDrag(key: string, sourceShapeIRI?: string): void {
    if (!this.cs.isPressed('n')) {
      this.startConstraintDrag(key, sourceShapeIRI || this.IRI);
    }

    if (!this.isClosed && key == 'section.0') {
      return this.startBaseDrag(key, { [sourceShapeIRI || this.IRI]: true });
    }

    const index = +key.split('.')[1];
    if (this.isClosed) {
      if (index == 0) {
        this.startBaseDrag(key, { [sourceShapeIRI || this.IRI]: true }, true);
        this.firstSectionDrag = true;
        this.dragSectionStartIndex = 1;
        this.dragSectionEndIndex = 1000;
      } else {
        this.dragSectionStartIndex = index;
        this.dragSectionEndIndex = index;
      }
    } else {
      this.dragSectionStartIndex = index;
      this.dragSectionEndIndex = index + 1;
    }

    this.baseConfigs = this.handSections
      .slice(this.dragSectionStartIndex, this.dragSectionEndIndex + 1)
      .map(hs => [hs.x, hs.y]);

    if (!this.isClosed && index == 1 && !this.cs.isPressed('n')) {
      this.startConstraintDrag('section.2', sourceShapeIRI || this.IRI);
    }
  }
  sectionChanged = false;
  _dragBy(
    constraint: DragConstraintInstance,
    { dx, dy, dAngle }: { dx: number; dy: number; dAngle?: number },
    root = false,
  ) {
    // -- // -- //
    const { point, noTranslate } = constraint;

    // -- // -- // -- //
    const [, _index, subIndex] = point.split('.');

    const index = +[_index, subIndex].filter(v => !!v).join('.');

    switch (index) {
      case 0:
        this.dx = dx;
        this.dy = dy;
        const [x, y] = this.dragBase;

        this.applyTranslate({
          x: x + dx,
          y: y + dy,
        });

        break;
      default:
        if (index % 1) {
          break;
        }
        // console.log('index', index, { dx, dy }, constraint.noAngle);

        [dx, dy] = this.handSections[index]._drag(
          constraint,
          {
            dx,
            dy,
            dAngle,
          },
          root,
        );
        // ∂console.log('post', dx, dy);
        break;
    }

    if (this.sectionChanged) {
      this.refreshElement();
    }

    super._dragBy(constraint, { dx, dy, dAngle }, root);
  }

  _endDragBy(constraint: DragConstraintInstance): void {
    const { point } = constraint;
    const index = +point.split('.')[1];

    switch (index) {
      case 0:
        this.saveTranslate(this.dx, this.dy);
        break;
      default:
        break;
    }

    // This is set during the _dragBy invocation
    if (this.sectionChanged) {
      this.saveSections();
    }
    super._endDragBy(constraint);

    this.dx = 0;
    this.dy = 0;
  }
  _drag(x: number, y: number, dx: number, dy: number) {
    this.constrainedShapes.map(shape => shape._drag(x, y, dx, dy));

    if (this.dragSectionStartIndex == undefined) {
      this.firstHandSection.pc.patch({
        p: [0, 0],
      });
      this.drag(dx, dy);
      return;
    }

    if (this.firstSectionDrag) {
      this.drag(dx, dy);
    }

    this.handSections
      .slice(this.dragSectionStartIndex, this.dragSectionEndIndex + 1)
      .map((hs, i) => {
        const [x, y] = this.baseConfigs[i];
        hs.x = x + (this.firstSectionDrag ? -dx : dx);
        hs.y = y + (this.firstSectionDrag ? -dy : dy);
        hs.refreshPCs();
      });

    this.handSections.map(hs => hs.refreshPCs());
    this.refreshElement();
  }

  _endDrag() {
    this.endDrag();

    this.firstSectionDrag = false;
    this.dragSectionStartIndex = undefined;
    this.dragSectionEndIndex = undefined;
    this.saveSections();
  }

  saveSections() {
    this.store.dispatch(
      setDescriptorValue({
        IRI: this.IRI,
        key: 'config',
        innerKey: 'handSections',
        value: cloneDeep(this.handSectionDescriptors),
      }),
    );
  }

  strokeElement: PathElement;
  strokeElements: PathElement[] = [];

  afterInit() {
    super.afterInit();
    // this._config = this.descriptor._config;

    // if (this._config.constraintTo) {
    //   const { shapeIRI, point } = this._config.constraintTo;
    //   this.constrainedShape = this.service.getShapeByIRI(shapeIRI);
    //   this.constraintPoint = point;
    //   if (this.constrainedShape) {
    //     this.constrainedShape.constrainedBy = point;

    //     if (this.constrainedShape instanceof HandShapeNextNext) {
    //       const [_x, _y] = this.xy;

    //       this.constrainedShape.applyShapeTranslate({
    //         x: this.x + _x,
    //         y: this.y + _y,
    //       });
    //     }
    //   }
    // }

    // this.turnPC.hide(); //
    this.refreshElement();
  }

  completeSelect(): void {
    this.handSections.map(hs => hs.showRC());
    this.baseLine.hide();
  }

  showDragControllers(): void {
    // DragPC can be removed
    this.handSections.map(hs => hs.showRC());
  }

  hideDragControllers(): void {
    this.handSections.map(hs => hs.hideRC());
  }

  dragOntoHandler() {
    // -- // -- // -- //
  }

  refreshElement(start?: number, end?: number): void {
    // -- // -- // -- //
    super.refreshElement();
    // -- // -- // -- //

    this.handSections?.map(hs => hs.initStrokeElements());
    this.handSections?.map(hs => hs.refreshPCs());
    // if (this.handSections) {
    //   const strokeSections = [];
    //   this.handSections.map((_hs, index) => {
    //     strokeSections.push([index, 'side']);
    //     strokeSections.push([index, 'bottom']);
    //   });

    //   if (!this.handIsClosed) {
    //     for (let i = this.handSections.length - 1; 0 < i; i--) {
    //       strokeSections.push([i, 'top']);
    //     }
    //   }

    //   this.strokeElements?.map(se => se.remove());
    //   this.strokeElements = [];
    // }
  }

  _getElements(start?: number, end?: number) {
    return this._elements;
  }

  get _elements(): PathItem[] {
    const _elements = [];

    if (!this.handSections) {
      return [];
    }

    this.handSections.map(section => section.calcAngles());
    this.handSections.map(section =>
      _elements.push(...section.getForwardElements()),
    );

    if (!this.isClosed) {
      this.handSections
        .reduce((array, section) => {
          array.unshift(section);
          return array;
        }, [])
        .map(section => _elements.push(...section.getBackwardElements()));
    }

    // console.log('_element', _elements);
    return _elements;
  }

  getAngle(x: number, y: number) {
    if (!x && !y) {
      return 0;
    }
    const angle = Math.abs(Math.atan(y / x));
    if (x > 0 && y > 0) {
      return angle;
    }
    if (x < 0 && y > 0) {
      return PI - angle;
    }
    if (x < 0 && y < 0) {
      return PI + angle;
    }
    if (x > 0 && y < 0) {
      return 2 * PI - angle;
    }
    if (Math.abs(x) === 0) {
      return y > 0 ? PI / 2 : (3 * PI) / 2;
    }
    if (Math.abs(y) === 0) {
      return x > 0 ? 0 : PI;
    }
  }
}
